import type { Comic } from '@panels/shared';

/**
 * `panels_{title}_{artists}_{YYYY-MM-DD}.png` (Round 2 §2.2), e.g.
 * `panels_the-horse-incident_Finn-Dardy-Rico_2026-09-25.png`.
 *
 * Artists are listed in panel order, repeats included (a skip can give one person
 * two panels); an unfinished panel has no artist, so it adds nothing. The date is
 * the day the comic was finished, in the viewer's time zone.
 */
export function comicFilename(
  comic: Pick<Comic, 'title' | 'panels' | 'completedAt'>,
  today: Date = new Date(),
): string {
  const title = slug(comic.title ?? '') || 'untitled';

  const artists = [...comic.panels]
    .sort((a, b) => a.position - b.position)
    .map((panel) => (panel.author ? nameSlug(panel.author.displayName) : ''))
    .filter(Boolean)
    .join('-');

  const finished = comic.completedAt ? new Date(comic.completedAt) : null;
  const day = localDate(finished && !Number.isNaN(finished.getTime()) ? finished : today);

  // Long names give way before the title or the date do.
  const budget = MAX_BASENAME - `panels_${title}__${day}`.length;
  const fitted = artists.slice(0, Math.max(0, budget)).replace(/-+$/g, '');

  return ['panels', title, fitted, day].filter(Boolean).join('_') + '.png';
}

/** Comfortably inside every filesystem's 255-byte limit, even with multi-byte letters. */
const MAX_BASENAME = 120;

/** Lowercase, hyphen-separated, letters and digits of any script. */
function slug(text: string): string {
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
}

/**
 * Names keep their case ("Finn", not "finn"). Anything a filesystem dislikes goes,
 * as do spaces and underscores, since `_` separates the fields and `-` the names.
 */
function nameSlug(name: string): string {
  return name
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}-]+/gu, '')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
}

function localDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
